import AttachmentIcon from "@mui/icons-material/Attachment";
import CancelIcon from "@mui/icons-material/Cancel";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Badge,
  Box,
  FormHelperText,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { useDispatch, useSelector } from "react-redux";
import AppContainer from "../../../components/AppContainer";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import ChangePasswordDialog from "../../../components/Dialog/ChangePasswordDialog";
import ProfileSaveDialog from "../../../components/Dialog/ProfileSaveDialog";
import RomanianPhoneInput from "../../../components/romaniaPhoneInput";
import UploadImageAvatar from "../../../components/UploadImage";
import appcolors from "../../../constants/Colors";
import globalStyle from "../../../globalStyle";
import { setUserData } from "../../../lib/slices/userSlice";
import { baseUrl } from "../../../services";
import { getAddressDetailByPlaceId } from "../../../services/common/CommonApiFn";
import {
  changePasswordCompany,
  updateCompanyProfile,
} from "../../../services/company/authenticationApiFn";
import { encryptPassword, handleOpenSnackBar } from "../../../utils";
import { updateCompanyProfileValidation } from "../../../utils/Validation";
import Label from "./../../../components/Label/index";
import TextInput from "./../../../components/TextInputs/index";

function Profile() {
  const theme = useTheme(null);
  // changepassword dialog
  const openCpDialog = useRef(null);
  // profile save dialog
  const opensPsDialog = useRef(null);
  // profile save confirm dialog
  const opensSpcDialog = useRef(null);
  // verification dialog
  const opensVcpDialog = useRef(null);
  // verify confirm dialog
  const opensVcDialog = useRef(null);
  // Attachment image ref
  const attachmentRef = useRef(null);

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [inputValues, setInputValues] = useState({
    image: "",
    name: "",
    email: "",
    contact: "",
    location: "",
    source: "",
    licenseImg: "",
    facebookLink: "",
    instagramLink: "",
    youtubeLink: "",
    linkedInLink: "",
    cui: "",
  });

  const [inputError, setInputError] = useState({
    image: false,
    name: false,
    email: false,
    isEmailValid: false,
    contact: false,
    location: false,
    source: false,
    licenseImg: false,
  });

  const userData = useSelector((state) => state.userData.user);

  useEffect(() => {
    if (userData) {
      setInputValues((prev) => ({
        ...prev,
        image: userData.profileImg,
        name: userData.name,
        email: userData.email,
        contact: userData.contact,
        location: userData.location,
        source: userData.expertise,
        licenseImg: userData?.licenseImg,
        facebookLink: userData?.facebookLink,
        instagramLink: userData?.instagramLink,
        youtubeLink: userData?.youtubeLink,
        linkedInLink: userData?.linkedInLink,
        cui: userData?.cui || "",
      }));
      setLocation(userData?.location);
    }
  }, [userData]);

  // Render top right part on template
  const renderRightPart = () => {
    return (
      <Stack
        gap={2}
        direction={"row"}
        alignItems={"center"}
        flex={1}
        justifyContent={"flex-end"}
      >
        <Box height={40}>
          <GlobalButton
            variant={"outlined"}
            btnText={"resetPassword"}
            styleProps={{ width: "auto" }}
            handleClickBtn={() => openCpDialog.current.openDialog()}
          />
        </Box>
        <Box width={100} height={40}>
          <GlobalButton
            variant={"contained"}
            btnText={"save"}
            styleProps={{ fontWeight: 400 }}
            handleClickBtn={() => {
              const validate = updateCompanyProfileValidation(
                inputValues,
                setInputError
              );
              if (!validate) {
                opensPsDialog.current.openDialog();
              }
            }}
          />
        </Box>
      </Stack>
    );
  };

  const handleImageChange = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      setInputError((prevError) => ({
        ...prevError,
        image: false,
      }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handlUploadAttachment = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((preValues) => ({
        ...preValues,
        license: response.data.url,
      }));
      setInputError((preError) => ({ ...preError, licenseImg: false }));
      attachmentRef.current.value = null;
      handleOpenSnackBar("success", "Image uploaded successfully!");
    } catch (error) {
      handleOpenSnackBar("error", "Image uploading failed!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;

    setInputError((prevError) => ({
      ...prevError,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
    }));
    setInputValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async (password) => {
    const longitude = location?.lng || location.coordinates[0];
    const latitude = location?.lat || location.coordinates[1];

    const locationObject = {
      addressLabel:
        inputValues.location.label || inputValues.location?.addressLabel,
      formatedAddress: location?.address || location?.formatedAddress,
      type: "Point",
      coordinates: [longitude, latitude],
    };
    let payload = {
      name: inputValues.name,
      contact: inputValues.contact,
      location: locationObject,
      expertise: inputValues.source,
      profileImg: inputValues.image,
      licenseImg: inputValues.licenseImg,
      password,
      // facebookLink: inputValues.facebookLink,
      // instagramLink: inputValues.instagramLink,
      // youtubeLink: inputValues.youtubeLink,
      // linkedInLink: inputValues.linkedInLink,
    };

    try {
      setIsLoading(true);
      const response = await updateCompanyProfile(userData?.adminId, payload);
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data, role: "company" };
        localStorage.setItem("user-data", JSON.stringify(updatedData));
        dispatch(setUserData(updatedData));
        handleOpenSnackBar("success", response.data.message);
        opensPsDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (data) => {
    try {
      const payload = {
        userId: userData?.adminId,
        newPassword: encryptPassword(data.newPassword),
        oldPassword: encryptPassword(data.oldPassword),
        role: "Company",
      };
      setIsLoading(true);
      const response = await changePasswordCompany(payload);
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        openCpDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlaceDetails = async (object) => {
    try {
      setIsLoading(true);
      const response = await getAddressDetailByPlaceId(object.value.place_id);

      if (response?.data?.status === "success") {
        const data = response.data.data;
        const lat = data.result.geometry.location.lat;
        const lng = data.result.geometry.location.lng;

        setLocation({
          address: data.result.formatted_address,
          lat,
          lng,
        });
      } else {
        setLocation(null);
        setInputError((err) => ({ ...err, location: false }));
        handleOpenSnackBar("error", t("placeDetailsUnavailable"));
      }
    } catch (error) {
      setInputError((err) => ({ ...err, location: false }));
      setLocation(null);
      handleOpenSnackBar("error", t("errorFetchingPlaceDetails"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <AppContainer
        primaryHeading={t("personalDetails")}
        secondaryHeading={t("accountDetailsInfo")}
        headProps={renderRightPart()}
        childrenProps={
          {
            // m: "16px 0",
          }
        }
      >
        <Typography
          variant="h1"
          fontWeight={600}
          fontSize={"1.125rem"}
          mb={3}
          textTransform={"capitalize"}
        >
          {t("personalInformation")}
        </Typography>

        <Grid container mt={5}>
          <Grid item xs={12} md={3}>
            <UploadImageAvatar
              error={inputError.image}
              image={inputValues.image}
              handleImageChange={handleImageChange}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={`${t("company")} ${t("name")}`}
                  required
                  htmlFor={"company-name"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"company-name"}
                  required
                  inputError={inputError.name}
                  helperText={inputError.name && t("companyNameRequired")}
                  placeholder={t("enterCompanyName")}
                  name={"name"}
                  value={inputValues.name}
                  onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={`${t("company")} ${t("email")}`}
                  required
                  htmlFor={"company-email"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"company-email"}
                  labelText={`${t("enter")} ${t("company")} ${t("email")}`}
                  inputError={inputError.email || inputError.isEmailValid}
                  helperText={
                    inputError.email
                      ? t("emailRequired")
                      : inputError.isEmailValid
                      ? t("invalidEmailFormat")
                      : null
                  }
                  required
                  readonly
                  name={"email"}
                  value={inputValues.email}
                  onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("contact")}
                  required
                  htmlFor={"business-contact"}
                />
                <RomanianPhoneInput
                  inputError={inputError.contact}
                  value={inputValues.contact}
                  inputStyle={{
                    borderColor: inputError?.contact
                      ? theme.palette.error.main
                      : theme.palette.custom.slateGrey,
                  }}
                  buttonStyle={{
                    borderColor: inputError?.contact
                      ? theme.palette.error.main
                      : theme.palette.custom.slateGrey,
                  }}
                  onChange={(value) => {
                    setInputValues((prev) => ({ ...prev, contact: value }));
                    setInputError((err) => ({ ...err, contact: false }));
                  }}
                />
                {/* <TextInput
                  styleProps={globalStyle.textField}
                  id={"business-contact"}
                  placeholder={"Enter contact"}
                  inputError={inputError.contact}
                  helperText={
                    inputError.contact && "Contact number is required"
                  }
                  required
                  name={"contact"}
                  value={inputValues.contact}
                  onChange={handleChangeInputValues}
                /> */}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack
                  mb={"5px"}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Label
                    labelText={t("searchLocation")}
                    htmlFor={"location"}
                    required
                    labelProps={{ marginBottom: 0 }}
                  />
                  <Stack direction={"row"}>
                    <Label
                      labelText={t("current")}
                      htmlFor={"location"}
                      labelProps={{ marginBottom: 0, fontSize: "0.775rem" }}
                    />
                    <LocationOnOutlinedIcon
                      htmlColor={appcolors.black}
                      sx={{ fontSize: "1.2rem" }}
                    />
                  </Stack>
                </Stack>

                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
                  selectProps={{
                    styles: {
                      control: (provided, state) => ({
                        ...provided,
                        padding: "4px",
                        borderRadius: "6px",
                        borderColor: inputError.location
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                        boxShadow: state.isFocused
                          ? `0 0 0 1px ${theme.palette.primary.main}`
                          : "none",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                      }),
                    },
                    value: location?.addresss,
                    onChange: (e) => {
                      setInputError((err) => ({ ...err, location: false }));
                      setInputValues((prev) => ({ ...prev, location: e }));
                      fetchPlaceDetails(e);
                    },
                  }}
                />

                {inputError.location && (
                  <FormHelperText sx={{ color: "#d32f2f" }}>
                    {t("selectYourLocation")}
                  </FormHelperText>
                )}
              </Grid>
              {location && (
                <Grid item xs={12} md={6}>
                  <Label
                    labelText={t("selectedAddress")}
                    htmlFor={"location"}
                    labelProps={{ mt: 1 }}
                  />
                  <TextInput
                    id={"location"}
                    required={true}
                    inputError={inputError.location}
                    helperText={inputError.location && t("locationRequired")}
                    placeholder={`${t("enter")} ${t("location")}`}
                    type={"text"}
                    value={
                      inputValues.location?.label ||
                      inputValues.location?.addressLabel
                    }
                    readonly
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Label
                  labelText={`${t("cui")} (${t("startsWithRO")})`}
                  required
                  htmlFor={"cui"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"cui"}
                  placeholder={`${t("enter")} ${t("cui")}`}
                  readonly
                  value={inputValues.cui}
                />
              </Grid>

              <Grid item xs={12}>
                <Label
                  labelText={`${t("company")} ${t("bio")}`}
                  required
                  htmlFor={"company-bio"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"company-bio"}
                  placeholder={`${t("enter")} ${t("bio")}`}
                  inputError={inputError.source}
                  helperText={
                    inputError.source && `${t("company")} ${t("bioRequired")}`
                  }
                  required
                  name={"source"}
                  value={inputValues.source}
                  onChange={handleChangeInputValues}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <Label
                  labelText={t("license")}
                  required
                  htmlFor={"business-source"}
                />
                <Box>
                  <input
                    ref={attachmentRef}
                    type="file"
                    accept="image/*"
                    onChange={handlUploadAttachment}
                    style={{ display: "none" }}
                  />
                  <GlobalButton
                    variant={"outlined"}
                    btnText={"attach"}
                    startIcon={<AttachmentIcon />}
                    handleClickBtn={() => attachmentRef.current.click()}
                    styleProps={{
                      width: "max-content",
                      height: 40,
                      borderWidth: 1,
                      borderRadius: "6px",
                      color: inputError.licenseImg
                        ? "custom.errorRed"
                        : "primary.main",
                      borderColor: inputError.licenseImg
                        ? "custom.errorRed"
                        : "primary.main",
                    }}
                    hoverProperties={{
                      borderWidth: 1,
                    }}
                  />
                  {inputError.licenseImg && (
                    <FormHelperText sx={{ color: "custom.errorRed" }}>
                      {t("attachmentRequired")}
                    </FormHelperText>
                  )}
                  {inputValues.licenseImg && (
                    <Badge
                      sx={{
                        "& .MuiBadge-badge": {
                          top: "4%",
                          right: "4%",
                        },
                      }}
                      overlap="circular"
                      badgeContent={
                        <CancelIcon
                          onClick={() => {
                            setInputValues((pre) => ({
                              ...inputValues,
                              licenseImg: null,
                            }));
                          }}
                          sx={{
                            fontSize: "1.125rem",
                            color: "custom.errorRed",
                            cursor: "pointer",
                          }}
                        />
                      }
                    >
                      <Box
                        position={"relative"}
                        p={0.8}
                        border={"1px solid grey"}
                        borderRadius={2}
                        ml={1}
                        px={3}
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          window.open(inputValues.licenseImg, "_blank");
                        }}
                      >
                        <Typography variant="body1">{t("view")}</Typography>
                      </Box>
                    </Badge>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* <Grid container mt={3} spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
              textTransform={"capitalize"}
            >
              {t("facebookPageLink")}
            </Typography>
            <Label labelText={t("link")} htmlFor={"sp-fb-link"} />
            <TextInput
              placeholder={`${t("enter")} ${t("facebookPageLink")}`}
              id={"sp-fb-link"}
              value={inputValues.facebookLink}
              name={"facebookLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
              textTransform={"capitalize"}
            >
              {t("instagramLink")}
            </Typography>
            <Label labelText={t("link")} htmlFor={"sp-ig-link"} />
            <TextInput
              placeholder={`${t("enter")} ${t("instagramLink")}`}
              id={"sp-ig-link"}
              value={inputValues.instagramLink}
              name={"instagramLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
            >
              {t("youtubeLink")}
            </Typography>
            <Label labelText={t("link")} htmlFor={"sp-yt-link"} />
            <TextInput
              placeholder={`${t("enter")} ${t("youtubeLink")}`}
              id={"sp-yt-link"}
              value={inputValues.youtubeLink}
              name={"youtubeLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
            >
              {t("linkedInLink")}
            </Typography>
            <Label labelText={t("link")} htmlFor={"sp-lk-link"} />
            <TextInput
              placeholder={`${t("enter")} ${t("linkedInLink")}`}
              id={"sp-lk-link"}
              value={inputValues.linkedInLink}
              name={"linkedInLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
        </Grid> */}
      </AppContainer>

      {/* Profile save dialog */}

      <ProfileSaveDialog
        ref={opensPsDialog}
        handleSaveProfile={handleSaveProfile}
      />

      <ChangePasswordDialog
        ref={openCpDialog}
        handleChangePassword={handleChangePassword}
      />

      {/* <SaveProfileConformDialog ref={opensSpcDialog} />
      <VerificationDialog
        ref={opensVcpDialog}
        openSuccessDialog={opensVcDialog}
      />
      <VerifyConfirmDialog ref={opensVcDialog} /> */}
    </>
  );
}

export default Profile;
